import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Users, Layers, IndianRupee, CreditCard,
  MessageSquare, Trophy, ArrowRight,
  GraduationCap, Clock, Calendar,
  UserPlus, BookOpen, Receipt, MessageSquarePlus, MessageCircle, BarChart3
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import StatCard from "./StatCard";
import RecentPayments from "./RecentPayments";
import UpcomingBatches from "./UpcomingBatches";
import UpgradeBanner from "./UpgradeBanner";
import GracePeriodBanner from "./GracePeriodBanner";
import RevenueShareBanner from "./RevenueShareBanner";
import LiveClassesCard from "./LiveClassesCard";
import BranchComparison from "./BranchComparison";
import DashboardEmpty from "./DashboardEmpty";
import TodaySchedule from "./UpcomingInterviews";
import DashboardAvatar from "./DashboardAvatar";
import Shimmer from "../Common/Shimmer";
import apiClient from "../../utils/apiClient";
import { useNavigate } from "react-router-dom";
import { convertToIndianCurrency, timeAgoIST } from "../../utils/common";
import { useBranch } from "../../contexts/BranchContext";
import { useTokenColors } from "../../utils/themeColor";
import "./dashboard.scss";

// Preset date-range options for the selector
const RANGE_PRESETS = [
  { key: "1d", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "month", label: "This month" },
  { key: "90d", label: "Last 90 days" },
  { key: "custom", label: "Custom range" },
];

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

// Compute {from, to} ISO strings for a given preset key (+ custom dates).
function resolveRange(key, customFrom, customTo) {
  const now = new Date();
  switch (key) {
    case "1d":
      return { from: startOfDay(now).toISOString(), to: endOfDay(now).toISOString() };
    case "7d": {
      const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6));
      return { from: start.toISOString(), to: endOfDay(now).toISOString() };
    }
    case "90d": {
      const start = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89));
      return { from: start.toISOString(), to: endOfDay(now).toISOString() };
    }
    case "custom": {
      if (customFrom && customTo) {
        return {
          from: startOfDay(new Date(customFrom)).toISOString(),
          to: endOfDay(new Date(customTo)).toISOString(),
        };
      }
      // Fall back to current month until both custom dates are picked.
      return resolveRange("month");
    }
    case "month":
    default: {
      const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      const end = endOfDay(now);
      return { from: start.toISOString(), to: end.toISOString() };
    }
  }
}

// Local YYYY-MM-DD for <input type="date"> values.
const toDateInputValue = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${m}-${day}`;
};

// ── Helpers ──
function formatCompact(amount) {
  if (!amount || isNaN(amount)) return "₹0";
  if (amount >= 100000) return `${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
  return `₹${amount}`;
}

/**
 * The dashboard's chart inks, as TOKEN NAMES.
 *
 * ⚠️ TWO DIFFERENT MECHANISMS ARE IN PLAY HERE and mixing them up is how this
 * file shipped broken. recharts sets `stroke` / `fill` / a tick's `fill` as SVG
 * PRESENTATION ATTRIBUTES, where `var()` does NOT resolve; `contentStyle` and a
 * React `style={{}}` are ordinary CSS declarations, where it does. So:
 *
 *   <CartesianGrid stroke="var(--border-primary, #e5e7eb)" />   BROKEN
 *   <Tooltip contentStyle={{ background: "var(--card-bg)" }} /> fine
 *
 * The grid on both charts and every axis tick label were written the first way.
 * The attribute is an unparseable colour, so the browser ignores it and the
 * element falls back to its SVG default — stroke `none` for the grid (invisible
 * lines, in both themes) and black for the tick text (unreadable in dark mode).
 * TOKEN_CONTRACT §4.5 has the mechanism; utils/themeColor.js is the way through,
 * and it re-reads when the theme flips or branding rewrites --primary.
 *
 * `collected` is --primary rather than a ramp hue on purpose: it is the headline
 * series on the institute's own dashboard, so it should carry their brand.
 */
const CHART_TOKENS = {
  collected: "--primary",
  pending: "--chart-3",
  grid: "--border-primary",
  tick: "--text-tertiary",
  // The white centre of a hovered point. Was "#fff", which is the card colour in
  // light mode and a hole punched in the card in dark.
  dot: "--card-bg",
  // The hover wash behind a bar. Was rgba(0,0,0,0.03) — which is exactly what
  // --elevate-1 holds in light mode, and it inverts to rgba(255,255,255,0.04) in
  // dark, where darkening a bar does nothing. Read, not var()'d: recharts puts
  // `cursor` on an SVG <Rectangle>, so this is an attribute like the rest.
  cursor: "--elevate-1",
};

// Category ramp for the per-course bar chart. Seven distinct inks — the brand
// plus the six-hue data-viz ramp — cycled by the call site. It was eight hexes
// ending in an indigo that appears in no palette.
const BAR_TOKENS = ["--primary", "--chart-2", "--chart-1", "--chart-3", "--chart-4", "--chart-5", "--chart-6"];

// contentStyle IS a style object, so var() is correct here — no reader needed.
const tooltipStyle = {
  backgroundColor: "var(--card-bg)",
  border: "1px solid var(--border-primary)",
  borderRadius: "8px",
  fontSize: "12px",
  padding: "8px 12px",
  boxShadow: "0 2px 8px var(--card-shadow)",
  color: "var(--text-primary)",
};

// ── Revenue Chart ──
function RevenueChart({ data = [], hasEverCollected = true }) {
  const c = useTokenColors(CHART_TOKENS);
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon emerald"><IndianRupee size={18} /></div>
          <div>
            <div className="db-card-title">Revenue Overview</div>
            <div className="db-card-subtitle">Monthly fee collection trend</div>
          </div>
        </div>
        <div className="db-card-head-right">
          <div className="db-chart-legend">
            <span className="db-chart-legend-item">
              <span className="db-legend-dot" style={{ background: "hsl(var(--primary))" }} />Collected
            </span>
            <span className="db-chart-legend-item">
              <span className="db-legend-dot" style={{ background: "hsl(var(--chart-3))" }} />Pending
            </span>
          </div>
        </div>
      </div>
      <div className="db-card-body">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gradCollected" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={c.collected} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={c.collected} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradPending" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={c.pending} stopOpacity={0.1} />
                  <stop offset="95%" stopColor={c.pending} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: c.tick }} dy={8} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: c.tick }} tickFormatter={(v) => v >= 1000 ? `${v / 1000}K` : v} dx={-4} />
              <Tooltip contentStyle={tooltipStyle} formatter={(value) => [convertToIndianCurrency(value)]} />
              <Area type="monotone" dataKey="collected" stroke={c.collected} strokeWidth={2.5} fill="url(#gradCollected)" dot={false} activeDot={{ r: 5, strokeWidth: 2, fill: c.dot }} />
              {data[0]?.pending !== undefined && (
                <Area type="monotone" dataKey="pending" stroke={c.pending} strokeWidth={2} strokeDasharray="5 5" fill="url(#gradPending)" dot={false} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <DashboardEmpty
            isFirstTime={!hasEverCollected}
            firstTimeText="No fees recorded yet. Collect one and this chart starts tracking your monthly income."
            periodText="No fees collected in this period."
            action={{ label: "Record a payment", to: "/fees/new" }}
          />
        )}
      </div>
    </div>
  );
}

// ── Fee Collection Donut ──
function FeeCollectionChart({ collected, pending }) {
  const c = useTokenColors(CHART_TOKENS);
  const total = collected + pending;
  const percent = total > 0 ? Math.round((collected / total) * 100) : 0;
  const pieData = [
    { name: "Collected", value: collected || 0.01 },
    { name: "Pending", value: pending || 0.01 },
  ];
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon blue"><CreditCard size={18} /></div>
          <div>
            <div className="db-card-title">Fee Collection</div>
            <div className="db-card-subtitle">Overall collection status</div>
          </div>
        </div>
      </div>
      <div className="db-card-body">
        <div className="db-donut-wrapper">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={58}
                outerRadius={78}
                paddingAngle={3}
                dataKey="value"
                strokeWidth={0}
                startAngle={90}
                endAngle={-270}
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? c.collected : c.pending} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(value) => [convertToIndianCurrency(value)]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="db-donut-center">
            <div className="db-donut-percent">{percent}%</div>
            <div className="db-donut-label">Collected</div>
          </div>
        </div>
        <div className="db-donut-breakdown">
          <div className="db-donut-row">
            <div className="db-donut-row-left">
              <span className="db-legend-dot" style={{ background: "hsl(var(--primary))" }} />Collected
            </div>
            <span className="db-donut-row-val">{formatCompact(collected)}</span>
          </div>
          <div className="db-donut-row">
            <div className="db-donut-row-left">
              <span className="db-legend-dot" style={{ background: "hsl(var(--chart-3))" }} />Pending
            </div>
            <span className="db-donut-row-val">{formatCompact(pending)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Course Enrollment Bar Chart ──
function CourseEnrollmentChart({ data = [], hasCourses = true }) {
  const ink = useTokenColors(CHART_TOKENS);
  const bars = useTokenColors(Object.fromEntries(BAR_TOKENS.map((t, i) => [i, t])));
  const total = data.reduce((s, c) => s + c.students, 0);
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon violet"><Layers size={18} /></div>
          <div>
            <div className="db-card-title">Course-wise Enrollment</div>
            <div className="db-card-subtitle">Student distribution across courses</div>
          </div>
        </div>
        <span className="db-badge">{total} Total</span>
      </div>
      <div className="db-card-body">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }} barSize={40}>
              <CartesianGrid strokeDasharray="3 3" stroke={ink.grid} vertical={false} />
              <XAxis dataKey="course" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: ink.tick }} dy={8} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: ink.tick }} dx={-4} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: ink.cursor }} />
              <Bar dataKey="students" radius={[6, 6, 0, 0]}>
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.color || bars[i % BAR_TOKENS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <DashboardEmpty
            isFirstTime={!hasCourses}
            firstTimeText="No courses yet. A course is what batches, fees and enrolments hang off."
            periodText="No students enrolled in any course yet."
            action={hasCourses
              ? { label: "Add students", to: "/students/add" }
              : { label: "Add your first course", to: "/courses/add" }}
          />
        )}
      </div>
    </div>
  );
}

// ── Enquiry Funnel ──
// Distinct colour per pipeline stage, keyed by lowercased status.
//
// These are painted into a React style={{}}, NOT an SVG attribute, so var() is
// the right form here and no reader is needed — the split is attribute vs
// declaration, not JSX vs SCSS.
//
// The four outcome stages carry STATUS meaning ("dropped" is bad, "enrolled" is
// good) so they read the status families; the process stages are distinct rather
// than meaningful, so they read the data-viz ramp. Previously all ten were hexes,
// three of which (#16a34a, #ef4444, #f59e0b) were near-duplicates of a stage
// already in the list, in a slightly different shade.
const FUNNEL_COLORS = {
  enquired: "hsl(var(--primary))",
  confirmed: "hsl(var(--success))",
  dropped: "hsl(var(--danger))",
  postponed: "hsl(var(--warning))",
  // ⚠️ NOT hsl(): --text-tertiary is a HEX surface token, not a triplet. The two
  // shapes are the trap CLAUDE.md names — hsl(var(--text-tertiary)) computes to
  // hsl(#4d535c), which is invalid, and the browser drops the declaration.
  closed: "var(--text-tertiary)",
  converted: "hsl(var(--chart-1))",
  contacted: "hsl(var(--chart-5))",
  "demo done": "hsl(var(--chart-3))",
  enrolled: "hsl(var(--success))",
  "not interested": "hsl(var(--danger))",
};

function EnquiryFunnelCard({ data = [], hasEverHadEnquiries = true }) {
  const maxCount = data.length > 0 ? Math.max(...data.map(e => e.count), 1) : 1;
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon amber"><MessageSquare size={18} /></div>
          <div>
            <div className="db-card-title">Enquiry Pipeline</div>
            <div className="db-card-subtitle">This month's funnel</div>
          </div>
        </div>
      </div>
      <div className="db-card-body db-funnel-body">
        {data.length > 0 ? data.map((stage) => (
          <div key={stage.stage} className="db-funnel-item">
            <div className="db-funnel-label">
              <span className="db-funnel-stage">{stage.stage}</span>
              <span className="db-funnel-count">{stage.count}</span>
            </div>
            <div className="db-funnel-bar-track">
              <div
                className="db-funnel-bar-fill"
                style={{
                  width: `${(stage.count / maxCount) * 100}%`,
                  backgroundColor: FUNNEL_COLORS[(stage.stage || "").toLowerCase()] || stage.color
                }}
              />
            </div>
          </div>
        )) : (
          <DashboardEmpty
            isFirstTime={!hasEverHadEnquiries}
            firstTimeText="No enquiries yet. Every walk-in, call and web form belongs here, with an owner and a next action."
            periodText="No enquiries in this period."
            action={{ label: "Add an enquiry", to: "/enquiries/new" }}
          />
        )}
      </div>
    </div>
  );
}

// ── Recent Activity (mixed feed) ──
function RecentActivityCard({ students = [], payments = [], navigate, hasStudents = true }) {
  // Build a unified activity feed from students and payments
  const activities = [];

  students.forEach(s => {
    activities.push({
      id: `s-${s._id}`,
      type: "enrollment",
      icon: <GraduationCap size={14} />,
      msg: <><strong>{s.name}</strong> enrolled in <strong>{s.course?.name || "a course"}</strong></>,
      time: s.createdAt,
    });
  });

  payments.forEach(p => {
    activities.push({
      id: `p-${p._id}`,
      type: "payment",
      icon: <IndianRupee size={14} />,
      msg: <><strong>{p.studentId?.name || "Student"}</strong> paid <strong>{convertToIndianCurrency(p.currentPayment)}</strong> for <strong>{p.studentId?.course?.name || "a course"}</strong></>,
      time: p.date,
    });
  });

  // Sort by time descending, take top 5
  activities.sort((a, b) => new Date(b.time) - new Date(a.time));
  const topActivities = activities.slice(0, 5);

  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon primary"><Clock size={18} /></div>
          <div>
            <div className="db-card-title">Recent Activity</div>
            <div className="db-card-subtitle">Latest updates across the institute</div>
          </div>
        </div>
        <button className="db-card-link" onClick={() => navigate("/students")}>
          View All <ArrowRight size={12} />
        </button>
      </div>
      <div className="db-card-body">
        {topActivities.length > 0 ? topActivities.map((a) => (
          <div key={a.id} className="db-activity-item">
            <div className={`db-activity-icon ${a.type}`}>{a.icon}</div>
            <div className="db-activity-content">
              <div className="db-activity-msg">{a.msg}</div>
              <div className="db-activity-time">{timeAgoIST(a.time)}</div>
            </div>
          </div>
        )) : (
          <DashboardEmpty
            isFirstTime={!hasStudents}
            firstTimeText="Nothing has happened yet. Add a student and enrolments and payments start appearing here."
            periodText="No activity in this period."
            action={{ label: "Add a student", to: "/students/add" }}
          />
        )}
      </div>
    </div>
  );
}

// ── Top Performers ──
function TopPerformersCard({ students = [], navigate, hasStudents = true }) {
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon amber"><Trophy size={18} /></div>
          <div>
            <div className="db-card-title">Top Performers</div>
            <div className="db-card-subtitle">Based on overall score</div>
          </div>
        </div>
        <button className="db-card-link" onClick={() => navigate("/students")}>
          View All <ArrowRight size={12} />
        </button>
      </div>
      <div className="db-card-body">
        {students.length > 0 ? students.slice(0, 5).map((s, i) => (
          <div key={s._id || i} className="db-list-item">
            <div className="db-list-rank">{i + 1}</div>
            <DashboardAvatar name={s.name} />
            <div className="db-list-info">
              <div className="db-list-name">{s.name}</div>
              <div className="db-list-sub">{s.course?.name || ""}</div>
            </div>
            <div className="db-list-right">
              <div className="db-list-value">{s.score ? `${s.score}%` : "—"}</div>
              {s.score && <div className="db-list-value-sub">Score</div>}
            </div>
          </div>
        )) : (
          <DashboardEmpty
            isFirstTime={!hasStudents}
            firstTimeText="No students yet, so nobody to rank."
            periodText="No scores recorded yet."
            action={hasStudents
              ? { label: "Open assessments", to: "/assessments" }
              : { label: "Add your students", to: "/students/add" }}
          />
        )}
      </div>
    </div>
  );
}

// ── Batch Health ──
const BATCH_STATUS_LABEL = { "on-track": "On Track", behind: "Behind", attention: "Attention" };

function lastClassLabel(days) {
  if (days == null) return "no classes yet";
  if (days === 0) return "class today";
  if (days === 1) return "1d since class";
  return `${days}d since class`;
}

function BatchHealthCard({ data = [], navigate, hasBatches = true }) {
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon violet"><Layers size={18} /></div>
          <div>
            <div className="db-card-title">Batch Health</div>
            <div className="db-card-subtitle">Syllabus pace &amp; activity</div>
          </div>
        </div>
        <button className="db-card-link" onClick={() => navigate("/batches")}>
          View All <ArrowRight size={12} />
        </button>
      </div>
      <div className="db-card-body">
        {data.length > 0 ? data.map((b) => (
          <div key={b.id} className="db-bh-item" onClick={() => navigate(`/batches/${b.id}`)}>
            <div className="db-bh-top">
              <span className="db-bh-name">{b.name}</span>
              <span className={`db-bh-chip ${b.status}`}>{BATCH_STATUS_LABEL[b.status] || b.status}</span>
            </div>
            <div className="db-bh-progress-track">
              <div className={`db-bh-progress-fill ${b.status}`} style={{ width: `${b.syllabusPct}%` }} />
            </div>
            <div className="db-bh-meta">
              {b.syllabusCompleted}/{b.syllabusTotal} sessions · {b.assignments} assignment{b.assignments === 1 ? "" : "s"} · {lastClassLabel(b.daysSinceLastClass)}
            </div>
          </div>
        )) : (
          <DashboardEmpty
            isFirstTime={!hasBatches}
            firstTimeText="No batches yet. A batch is the group that actually meets — a timetable, a trainer and a roll."
            periodText="No batches are running right now."
            action={hasBatches
              ? { label: "Open batches", to: "/batches" }
              : { label: "Create your first batch", to: "/batches/add" }}
          />
        )}
      </div>
    </div>
  );
}

// ── Quick Actions ──
function QuickActionsCard({ navigate }) {
  const actions = [
    { label: "Add Student", icon: <UserPlus size={18} />, cls: "blue", to: "/students/add" },
    { label: "Add Course", icon: <BookOpen size={18} />, cls: "emerald", to: "/courses/add" },
    { label: "Create Batch", icon: <Layers size={18} />, cls: "rose", to: "/batches/add" },
    { label: "Record Payment", icon: <Receipt size={18} />, cls: "amber", to: "/fees/new" },
    { label: "Add Enquiry", icon: <MessageSquarePlus size={18} />, cls: "violet", to: "/enquiries/new" },
    { label: "WhatsApp", icon: <MessageCircle size={18} />, cls: "cyan", to: "/whatsapp" },
  ];
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-title">Quick Actions</div>
        </div>
      </div>
      <div className="db-card-body">
        <div className="db-quick-grid">
          {actions.map((a) => (
            <button key={a.label} className="db-quick-action" onClick={() => navigate(a.to)}>
              <span className={`db-quick-icon ${a.cls}`}>{a.icon}</span>
              <span className="db-quick-label">{a.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Top Performing Courses (table) ──
// Pass 1: enrollments from courseEnrollment. Revenue + conversion are added in pass 2.
function TopCoursesCard({ data = [], navigate, hasCourses = true }) {
  const rows = [...data].sort((a, b) => b.students - a.students).slice(0, 5);
  return (
    <div className="db-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className="db-card-icon violet"><BarChart3 size={18} /></div>
          <div>
            <div className="db-card-title">Top Performing Courses</div>
            <div className="db-card-subtitle">By enrollment</div>
          </div>
        </div>
        <button className="db-card-link" onClick={() => navigate("/courses")}>
          View All Courses <ArrowRight size={12} />
        </button>
      </div>
      <div className="db-card-body">
        {rows.length > 0 ? (
          <table className="db-courses-table">
            <thead>
              <tr>
                <th>Course</th>
                <th className="db-num">Enrollments</th>
                <th className="db-num">Revenue</th>
                <th className="db-num">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c, i) => (
                <tr key={c.course || i}>
                  <td>
                    <span className="db-course-dot" style={{ background: c.color }} />
                    {c.course}
                  </td>
                  <td className="db-num">{c.students}</td>
                  <td className="db-num db-muted">{c.revenue != null ? convertToIndianCurrency(c.revenue) : "—"}</td>
                  <td className="db-num db-muted">{c.conversionRate != null ? `${c.conversionRate}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <DashboardEmpty
            isFirstTime={!hasCourses}
            firstTimeText="No courses yet. Add one and this table starts ranking them by enrolment and revenue."
            periodText="No enrolments to rank yet."
            action={hasCourses
              ? { label: "Open courses", to: "/courses" }
              : { label: "Add your first course", to: "/courses/add" }}
          />
        )}
      </div>
    </div>
  );
}

// ── Loading Skeleton (shimmer placeholders mirroring the layout) ──
function SkelCard({ height }) {
  return (
    <div className="db-card">
      <div className="db-card-body">
        <Shimmer height={height} />
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="db-page">
      <div className="db-grid-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <StatCard key={i} loading />
        ))}
      </div>

      <div className="db-grid-2-1-1">
        <SkelCard height={300} />
        <SkelCard height={300} />
        <SkelCard height={300} />
      </div>

      <div className="db-grid-2-1-1">
        <SkelCard height={280} />
        <SkelCard height={280} />
        <SkelCard height={280} />
      </div>

      <div className="db-grid-2">
        <SkelCard height={260} />
        <SkelCard height={260} />
      </div>

      <div className="db-grid-3">
        <SkelCard height={240} />
        <SkelCard height={240} />
        <SkelCard height={240} />
      </div>

      <div className="db-grid-2">
        <SkelCard height={240} />
        <SkelCard height={240} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// Main Dashboard
// ═══════════════════════════════════════
const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const { selectedBranchId } = useBranch();

  const [rangeKey, setRangeKey] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const range = useMemo(
    () => resolveRange(rangeKey, customFrom, customTo),
    [rangeKey, customFrom, customTo]
  );

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get("/dashboard", {
          params: { from: range.from, to: range.to },
        });
        setDashboard(data);
      } catch {
        setDashboard(null);
      }
      setLoading(false);
    };
    fetchDashboard();
  }, [selectedBranchId, range.from, range.to]);

  // ── Date range selector: injected into the top nav bar (right portal) ──
  const [dateRangeTarget, setDateRangeTarget] = useState(null);
  useEffect(() => {
    setDateRangeTarget(document.getElementById("top-nav-portal"));
  }, []);

  const headerPortals = dateRangeTarget && createPortal(
    <div className="db-date-range">
      <Calendar size={15} />
      <select
        className="db-date-range-select"
        value={rangeKey}
        onChange={(e) => setRangeKey(e.target.value)}
        aria-label="Date range"
      >
        {RANGE_PRESETS.map((o) => (
          <option key={o.key} value={o.key}>{o.label}</option>
        ))}
      </select>
      {rangeKey === "custom" && (
        <div className="db-date-range-custom">
          <input
            type="date"
            className="db-date-range-input"
            value={customFrom || toDateInputValue(range.from)}
            max={customTo || undefined}
            onChange={(e) => setCustomFrom(e.target.value)}
            aria-label="From date"
          />
          <span className="db-date-range-sep">–</span>
          <input
            type="date"
            className="db-date-range-input"
            value={customTo || toDateInputValue(range.to)}
            min={customFrom || undefined}
            onChange={(e) => setCustomTo(e.target.value)}
            aria-label="To date"
          />
        </div>
      )}
    </div>,
    dateRangeTarget
  );

  if (loading)
    return (
      <>
        {headerPortals}
        <DashboardSkeleton />
      </>
    );

  if (!dashboard)
    return (
      <>
        {headerPortals}
        <div className="db-page">
          <div className="db-empty-state">
            <p>Failed to load dashboard data. Please try again.</p>
          </div>
        </div>
      </>
    );

  /**
   * Institute-level facts, read ONCE so every empty state agrees.
   *
   * ⚠️ These come from lifetime totals in `stats`, never from the filtered
   * arrays the cards are rendering. The filtered array being empty is the thing
   * being explained, so it cannot also be the evidence — using it would tell a
   * year-old institute to "add your first course" the moment they picked
   * "Today" on the range selector.
   */
  const {
    stats = {}, courseEnrollment = [], revenueData = [], feeCollection = {},
    enquiryFunnel = [], todaySchedule = [], upcomingBatches = [],
    liveClasses = null, recentStudents = [], recentPayments = [],
    topCourses = [], batchHealth = []
  } = dashboard;

  const hasCourses = (stats.activeCourses || 0) > 0;
  const hasStudents = (stats.totalStudents || 0) > 0;
  const hasBatches = (stats.totalBatches ?? stats.activeBatches ?? 0) > 0;
  const hasEverCollected = (stats.totalCollectedFees || 0) > 0;
  const hasEverHadEnquiries = (stats.totalEnquiries ?? stats.newEnquiries ?? 0) > 0;

  return (
    <>
      {headerPortals}
      <div className="db-page">

      {/* ── Grace Period / Expiry Banner ── */}
      <GracePeriodBanner />

      {/* ── Revenue-Share Unpaid Invoice Banner ── */}
      <RevenueShareBanner />

      {/* ── Upgrade Upsell Banner ── */}
      <UpgradeBanner />

      {/* ── KPI Cards ── */}
      <div className="db-grid-4">
        <StatCard
          title="TOTAL STUDENTS"
          value={stats.totalStudents || 0}
          change={stats.studentsGrowth || null}
          changeType={stats.studentsGrowthPct != null && stats.studentsGrowthPct < 0 ? "negative" : "positive"}
          icon={<Users size={20} />}
          iconClass="blue"
          series={dashboard.sparklines?.students}
          onClick={() => navigate("/students")}
        />
        <StatCard
          title="ACTIVE BATCHES"
          value={stats.activeBatches || 0}
          change={null}
          changeType="positive"
          icon={<Layers size={20} />}
          iconClass="violet"
          series={dashboard.sparklines?.batches}
          onClick={() => navigate("/batches")}
        />
        <StatCard
          title="TOTAL REVENUE"
          value={formatCompact(stats.periodRevenue || 0)}
          change={stats.revenueGrowthPct != null ? `${Math.abs(stats.revenueGrowthPct)}%` : null}
          changeType={stats.revenueGrowthPct != null && stats.revenueGrowthPct < 0 ? "negative" : "positive"}
          icon={<IndianRupee size={20} />}
          iconClass="emerald"
          series={dashboard.sparklines?.revenue}
          onClick={() => navigate("/fees")}
        />
        <StatCard
          title="PENDING FEES"
          value={formatCompact(stats.totalPendingFees || 0)}
          change={`From ${stats.newEnquiries || 0} students`}
          changeType="negative"
          icon={<CreditCard size={20} />}
          iconClass="rose"
          onClick={() => navigate("/fees")}
        />
      </div>

      {/* ── Branch comparison ──
          Renders nothing for a single-branch institute, or for an admin whose
          scope is one branch — they have nothing to compare against. Sits
          directly under the KPI cards because "which branch is behind" is the
          first question a multi-centre owner asks, before any per-branch chart. */}
      <BranchComparison from={range.from} to={range.to} />

      {/* ── Revenue + Fee Collection + Live Classes ── */}
      <div className="db-grid-2-1-1">
        <RevenueChart data={revenueData} hasEverCollected={hasEverCollected} />
        <FeeCollectionChart
          collected={feeCollection?.collected || 0}
          pending={feeCollection?.pending || 0}
        />
        <LiveClassesCard data={liveClasses} navigate={navigate} />
      </div>

      {/* ── Course Enrollment + Enquiry Funnel + Quick Actions ── */}
      <div className="db-grid-2-1-1">
        <CourseEnrollmentChart data={courseEnrollment} hasCourses={hasCourses} />
        <EnquiryFunnelCard data={enquiryFunnel} hasEverHadEnquiries={hasEverHadEnquiries} />
        <QuickActionsCard navigate={navigate} />
      </div>

      {/* ── Top Performing Courses + Recent Activity ── */}
      <div className="db-grid-2">
        <TopCoursesCard data={topCourses.length ? topCourses : courseEnrollment} navigate={navigate} hasCourses={hasCourses} />
        <RecentActivityCard students={recentStudents} payments={recentPayments} navigate={navigate} hasStudents={hasStudents} />
      </div>

      {/* ── Top Performers + Upcoming Batches + Batch Health ── */}
      <div className="db-grid-3">
        <TopPerformersCard students={recentStudents} navigate={navigate} hasStudents={hasStudents} />
        <UpcomingBatches batches={upcomingBatches} />
        <BatchHealthCard data={batchHealth} navigate={navigate} hasBatches={hasBatches} />
      </div>

      {/* ── Today's Schedule + Recent Payments ── */}
      <div className="db-grid-2">
        <TodaySchedule schedule={todaySchedule} />
        <RecentPayments payments={recentPayments} />
      </div>
      </div>
    </>
  );
};

export default Dashboard;
